// Træneren: det vigtigste råd lige nu, som en ren funktion af state. UI'et viser rådet som hint,
// hånd og pulserende mål, og balance-målingen lader en "nybegynder" følge det. Så måler vi, at
// spillet kan vindes af en, der gør det spillet peger på.
//
// Reglerne for et godt råd:
// - Det kan gøres nu. Er der ikke råd, siger træneren "spar op til N kr" i stedet for at pege på
//   noget, der ikke kan lade sig gøre, og en ledning på højeste trin bliver aldrig foreslået opgraderet.
// - Tekst og hånd kommer fra det samme råd: en ledning har altid `from` og `to`.
// - Kul er reserve: et værk foreslås kun, når vind og sol ikke kan dække forbruget, og aldrig i
//   øvelsesåret, hvor mørkeurene står stille. Der lærer spilleren byg, orange, opgradér.
// - Rådet handler om det sted, spilleren kigger: flaskehalsen er den fulde ledning tættest på byen
//   (i hop), ikke den mest belastede et vilkårligt sted i nettet (playtest runde 2: "Esbjerg får kun
//   78 %. Opgradér Thor-Thorsminde").
// - Har kassen penge til overs i en rolig periode, foreslås vej nr. 2 til en by med én vej ind, eller et
//   batteri. Det giver pengene et formål, og fra Vagthavende er det også den tredje stjerne.
// - Når alt kører, roser træneren og fortæller, hvad der kommer.

import { clockFull, clocksFrozen, darkInTicks, isPowered, SERVED_OK_PM } from '../sim/cities';
import { crewKindFor } from '../sim/crews';
import { buildCost, degree, dist, slotsFree, heatAirPm, lineById, nodeById, nominalCap, pairKey, REINFORCE_COST, stationCost, upgradeCost } from '../sim/grid';
import { apply, check, exposedLines, REINFORCE_ALL } from '../sim/actions';
import { difficulty, greenGoal } from '../sim/difficulty';
import { dunkelStrength, fcrActive, heatKeepPm, heldLine } from '../sim/effects';
import { computeFlow } from '../sim/flow';
import { cloneState, greenPct, levelOf } from '../sim/state';
import { STAR_N1_TIER } from '../sim/step';
import { powerKind, powerReady, sheddable } from '../sim/powers';
import { CARD_COST, cardOpen, predictNadir, UFLS_CHZ } from '../sim/threats';
import type { ActionKind, Area, CardOption, CoachMode, GridNode, Intent, Line, LineMedium, PlayerId, PowerId, State, Threat } from '../sim/types';

/** Pengene på dansk: "gratis" i stedet for "for 0 kr" (PocketGamer, IGN-løkke runde 3). */
const kr = (n: number): string => (n <= 0 ? 'gratis' : `${n} kr`);
const forKr = (n: number): string => (n <= 0 ? 'gratis' : `for ${n} kr`);
/** Centihertz som dansk tekst: 4880 → "48,8 Hz". */
export const hz = (cHz: number): string => `${(cHz / 100).toFixed(cHz % 10 === 0 ? 1 : 2).replace('.', ',')} Hz`;

export type Advice =
  /** Et brud, og alle montører er optaget: hyr en mere. */
  | { kind: 'hire'; line: string; cost: number; urgent: true }
  /** Storm på vej: forstærk. `count` > 1: "Forstærk alle udsatte" i ét tryk for `cost` kr. */
  | { kind: 'reinforce'; line: string; count: number; cost: number; urgent: true }
  /** En ledning er knækket, og en montør er på vej: vent, byg ikke kul. `count`: knækkede i alt. */
  | { kind: 'broken'; line: string; count: number; urgent: true }
  | { kind: 'shoo'; threat: string; urgent: true }
  /** Et hændelseskort venter på svar. `opt` er det svar, træneren ville vælge. */
  | { kind: 'card'; threat: string; opt: CardOption; urgent: true }
  /** Brug en power-up nu. `target` er byen ved Nødaflastning. */
  | { kind: 'power'; power: PowerId; target: string | null; city: string; urgent: true }
  /** Træk en ledning fra `from` til `to`. `node` er byen eller producenten, det handler om. */
  //  `partial`: rådet hjælper, men byen får kun så mange procent bagefter. Så siger teksten det ærligt.
  | { kind: 'build'; why: 'city' | 'producer' | 'power' | 'backup' | 'second'; node: string; from: string; to: string; opt: LineMedium; cost: number; urgent: boolean; partial?: number }
  | { kind: 'upgrade'; line: string; node: string | null; cost: number; urgent: boolean; partial?: number; green?: boolean }
  /** Noden har ikke flere pladser: byg en station, så `for` kan komme på. */
  | { kind: 'station'; node: string; for: string; cost: number; urgent: boolean }
  /** Byg et batteri ved byen (gratis, når en gave venter). */
  | { kind: 'battery'; node: string; cost: number; urgent: false }
  /** Et værk er ved at falde ud, og dykket vil koble en by fra: byg et svinghjul i prisområdet. `nadir` i centihertz uden. */
  | { kind: 'flywheel'; node: string; cost: number; nadir: number; urgent: true }
  /** Det rigtige træk koster mere, end der er i kassen. */
  | { kind: 'save'; node: string; need: number; what: 'line' | 'upgrade' | 'station'; line: string | null; urgent: boolean }
  /** Byen får lidt for lidt, men uret står stille (første år): ingen grund til kul, det går over.
   *  `lack`: intet træk lige nu giver byen mere, fordi der ikke er mere strøm at hente. Det siges ærligt. */
  //  `soon`: byen får strøm, så snart flowet er regnet igen (en ledning er lige bygget).
  | { kind: 'calm'; node: string; urgent: false; lack?: boolean; soon?: boolean }
  /** Alt kører. `next` er den næste by, der dukker op. `dark`: `node` er mørk et øjeblik, så der roses ikke. */
  | { kind: 'praise'; node: string; next: string | null; urgent: false; dark?: boolean };

/** En producent, der er værd at spare op til (Thor Havvind). */
const BIG_PRODUCER_MW = 300;

function wanting(n: GridNode): boolean {
  return !!n.city && n.active && !n.phantom && n.city.darkTicks === 0 && !isPowered(n);
}

function hasAnyLine(state: State, id: string): boolean {
  return state.lines.some((l) => l.a === id || l.b === id);
}

/** Nettet over hele ledninger fra en node (inklusive noden). */
export function netOf(state: State, from: string): Set<string> {
  const seen = new Set<string>([from]);
  const queue = [from];
  while (queue.length > 0) {
    const u = queue.shift() as string;
    for (const l of state.lines) {
      if (l.status !== 'ok') continue;
      const v = l.a === u ? l.b : l.b === u ? l.a : null;
      if (v !== null && !seen.has(v)) {
        seen.add(v);
        queue.push(v);
      }
    }
  }
  return seen;
}

/** Et kul- eller gasværk (ikke et udlandskabel). */
export function isPlant(n: GridNode): boolean {
  return !!n.producer && n.producer.green < 0.5 && n.kind !== 'foreign';
}

/**
 * Må træneren foreslå et værk? Kun når vind og sol ikke kan dække forbruget, og den grønne andel har
 * luft til det: et værk på en bane, hvor man er tæt på det grønne mål, koster stjernen (playtest
 * runde 2: "Forbind Kolding til Skærbækværket" på en bane med grønt mål).
 */
function plantAdvisable(state: State): boolean {
  if (greenEnough(state)) return false;
  // Øveåret lærer vind og sol; et kraftværk er aldrig første råd.
  if (clocksFrozen(state, levelOf(state))) return false;
  const goal = greenGoal(levelOf(state).greenGoalPct, state.difficulty);
  return greenPct(state) >= goal + 8;
}

/** Kan vind, sol og havvind på kortet lige nu dække alle byers forbrug? Så er kul ikke et råd. */
export function greenEnough(state: State): boolean {
  let green = 0;
  let demand = 0;
  for (const n of state.nodes) {
    if (!n.active || n.phantom) continue;
    if (n.producer && !isPlant(n) && n.kind !== 'foreign') green += n.producer.availMw;
    if (n.city) demand += n.city.demandMw;
  }
  return green >= demand;
}

type Route =
  | { ok: true; from: string; to: string; opt: LineMedium; cost: number }
  | { ok: false; poor: number; full: string | null };

/**
 * Den bedste nye ledning fra `from` til en node, `accept` godtager: kortest, luft før søkabel før
 * jordkabel. Kan ingen bygges nu, siges hvad der mangler: penge (`poor` = billigste pris) eller en
 * plads (`full` = den nærmeste node uden plads).
 */
function routeFrom(state: State, from: string, accept: (o: GridNode) => boolean): Route | null {
  const p = nodeById(state, from);
  if (!p) return null;
  let best: { to: string; opt: LineMedium; cost: number; d: number } | null = null;
  let poor = Infinity;
  let full: { id: string; d: number } | null = null;
  for (const o of state.nodes) {
    if (!o.active || o.phantom || o.kind === 'foreign' || o.id === from || !accept(o)) continue;
    for (const opt of ['air', 'sea', 'cable'] as LineMedium[]) {
      const r = check(state, 'build_line', pairKey(from, o.id), opt);
      const d = dist(p, o) + (opt === 'air' ? 0 : opt === 'sea' ? 5 : 20);
      if (r.ok) {
        if (!best || d < best.d) best = { to: o.id, opt, cost: r.cost, d };
        break;
      }
      if (r.error === 'too_poor') {
        poor = Math.min(poor, buildCost(state, opt, Math.round(dist(p, o) * 10) / 10));
        break;
      }
      if (r.error === 'no_slots') {
        // Den fulde ende er den, der mangler plads. Står der allerede en station på den, hjælper en
        // station i den nye by ikke (før pegede rådet så på den nye by).
        const blocked = slotsFree(state, o) <= 0 ? o.id : from;
        if (!full || d < full.d) full = { id: blocked, d };
        break;
      }
    }
  }
  if (best) return { ok: true, from, to: best.to, opt: best.opt, cost: best.cost };
  if (poor < Infinity || full) return { ok: false, poor: poor < Infinity ? poor : 0, full: full?.id ?? null };
  return null;
}

/** En ledning er ikke mulig nu: sig hvad der skal til (station eller opsparing), ellers null. */
function blocked(state: State, r: Extract<Route, { ok: false }>, node: string, urgent: boolean): Advice | null {
  if (r.full) {
    const c = check(state, 'upgrade_station', r.full, undefined);
    if (c.ok) return { kind: 'station', node: r.full, for: node, cost: stationCost(state), urgent };
    if (c.error === 'too_poor') return { kind: 'save', node, need: stationCost(state), what: 'station', line: null, urgent };
  }
  if (r.poor > 0) return { kind: 'save', node, need: r.poor, what: 'line', line: null, urgent };
  return null;
}

/**
 * En ny by, der ikke kan komme på, fordi naboen er fuld (IGN-løkke 2, runde 3: "coachen skal sige det,
 * også på Systemansvarlig og Orkan, som en kort advarsel"). Spillet siger det på alle sværheder, så en
 * by aldrig går i sort, mens spilleren ikke ved hvorfor. `can`: station kan bygges nu, der skal spares
 * op, eller den fulde node har allerede en station (så må byen på fra en anden kant).
 */
export interface BlockedCity {
  city: string;
  full: string;
  cost: number;
  can: 'station' | 'save' | 'other';
}

export function blockedCity(state: State): BlockedCity | null {
  if (state.phase !== 'playing') return null;
  const plantOk = plantAdvisable(state);
  const lvl = levelOf(state);
  const darkIn = (n: GridNode): number => darkInTicks(state, lvl, n) ?? Number.MAX_SAFE_INTEGER;
  const lonely = state.nodes
    .filter((n) => n.city && (n.kind === 'city' || n.kind === 'datacenter') && n.active && !n.phantom && !hasAnyLine(state, n.id))
    .sort((a, b) => darkIn(a) - darkIn(b));
  for (const n of lonely) {
    const a = connect(state, n, 'city', true, plantOk);
    if (a && a.kind !== 'station' && !(a.kind === 'save' && a.what === 'station')) continue;
    const r = routeFrom(state, n.id, (o) => (o.producer ? (plantOk || !isPlant(o)) && !o.phantom : degree(state, o.id) > 0));
    if (!r || r.ok || !r.full || r.full === n.id) continue;
    const c = check(state, 'upgrade_station', r.full, undefined);
    const can = c.ok ? 'station' : c.error === 'too_poor' ? 'save' : 'other';
    return { city: n.id, full: r.full, cost: stationCost(state), can };
  }
  return null;
}

/** Den korte advarsel til en blokeret by. Navngiver altid den fulde node. */
export function blockedText(state: State, b: BlockedCity): string {
  const name = (id: string): string => nodeById(state, id)?.name ?? '';
  const head = `${name(b.full)} er fuld, så ${name(b.city)} kan ikke komme på.`;
  if (b.can === 'station') return `${head} Tryk på ${name(b.full)} og byg en station ${forKr(b.cost)}.`;
  if (b.can === 'save') return `${head} Spar op til en station i ${name(b.full)}, ${kr(b.cost)}.`;
  return `${head} Træk ${name(b.city)} fra en anden by, eller riv en af ${name(b.full)}s ledninger ned.`;
}

/** En by eller producent uden nogen ledning: træk en til nettet. */
function connect(state: State, n: GridNode, why: 'city' | 'producer', urgent: boolean, plantOk: boolean): Advice | null {
  // En by: hver kandidat regnes igennem på en kopi af nettet, og vi vælger den, der faktisk tænder byen.
  // Playtest runde 3: "Træk en ledning fra Ringkøbing til Hvide Sande Vind", og byen gik mørk med det
  // samme, fordi den lille mølle alene ikke kunne bære den.
  if (why === 'city') {
    // Grøn først: et værk kun, når ingen grøn vej tænder byen (IGN runde 3: på bane 1 pegede træneren
    // på Nordjyllandsværket, og den grønne andel faldt fra 100 til 61 %).
    const green = routesFrom(state, n.id, (o) => (o.producer ? !isPlant(o) : degree(state, o.id) > 0));
    const base = flowed(state);
    let pick = bestFor(state, n, green.map((r) => buildOption(n.id, r, n.id, 'city')), base);
    if ((!pick || pick.lift.pct * 1000 < SERVED_OK_PM) && plantOk) {
      const all = routesFrom(state, n.id, (o) => (o.producer ? true : degree(state, o.id) > 0));
      pick = bestFor(state, n, all.map((r) => buildOption(n.id, r, n.id, 'city')), base) ?? pick;
    }
    if (pick) return pickAdvice(n, pick, urgent);
  }
  const r = routeFrom(state, n.id, (o) => {
    // En by må gå til en producent; en producent må gå til alt, der allerede hænger i nettet.
    if (o.producer && why === 'city') return (plantOk || !isPlant(o)) && !o.phantom;
    return degree(state, o.id) > 0;
  });
  if (!r) return null;
  if (r.ok) return { kind: 'build', why, node: n.id, from: r.from, to: r.to, opt: r.opt, cost: r.cost, urgent };
  return blocked(state, r, n.id, urgent);
}

// ---------------------------------------------------------------------------
// Prøv rådet af, før det gives. Et råd, der ikke hjælper byen, er værre end intet råd (playtest runde 3:
// spillere fulgte hintet og mistede et hjerte).

/** Et muligt træk for en by i nød. */
interface Option {
  intent: Intent;
  cost: number;
  /** Afstand eller hop: lille er bedst ved lige gode træk. */
  d: number;
  affordable: boolean;
  /** Rådet, hvis trækket vælges. `partial`: byens procent bagefter, når den stadig ikke er tændt. */
  make: (urgent: boolean, partial: number | undefined) => Advice;
  /** Trækket kobler et kul- eller gasværk på: kun når intet grønt træk redder byen. */
  coal?: boolean;
}

/** Hvad et træk gør: byens andel af sin strøm bagefter (0-1), andre byer der går ud, og luft i byens net (0-1). */
interface Lift {
  pct: number;
  hurt: number;
  slack: number;
}

function servedPct(n: GridNode): number {
  const c = n.city;
  if (!c || c.demandMw <= 0) return 1;
  return Math.min(1, c.servedMw / c.demandMw);
}

/**
 * Nettet, som det står lige nu, med friskt flow. Flowet i state kan være et par ticks gammelt: en
 * ledning, der lige er bygget, bærer først ved næste beregning, og så så byen mørk ud, selv om hjælpen
 * var på plads (balance: eksperten byggede en ekstra vej til en by, der fik strøm i næste tick).
 */
function flowed(state: State): State {
  const s = cloneState(state);
  computeFlow(s);
  return s;
}

/**
 * Regn trækket igennem på en kopi af nettet med fuld kasse (så vi også ved, hvad der er værd at spare
 * op til), og se, hvad byen får bagefter. `base` er nettet før trækket med friskt flow. Ren funktion.
 */
function liftOf(state: State, base: State, intent: Intent, city: string): Lift | null {
  const s = cloneState(state);
  s.budget += 100_000;
  if (!apply(s, intent, []).ok) return null;
  computeFlow(s);
  const n = nodeById(s, city);
  if (!n?.city || n.city.demandMw <= 0) return null;
  let hurt = 0;
  for (const m of base.nodes) {
    if (!m.city || m.id === city || !m.active || m.phantom || m.city.demandMw <= 0 || !isPowered(m)) continue;
    const after = nodeById(s, m.id);
    if (after && !isPowered(after)) hurt++;
  }
  const net = netOf(s, city);
  let spare = 0;
  for (const m of s.nodes) if (m.producer && m.active && net.has(m.id)) spare += Math.max(0, m.producer.availMw - m.producer.usedMw);
  return { pct: servedPct(n), hurt, slack: Math.min(1, spare / n.city.demandMw) };
}

/** Højst så mange kandidater regnes igennem pr. råd: hver er en kopi af nettet og en flowberegning. */
const MAX_TRIES = 10;
/**
 * Spor: tillid (mobil runde 4: "Hjørring til Nørrekær, Herning til Høvsøre på tværs af kortet, selvom
 * der er en nabo lige ved siden af"). Hver SHORT_KM km koster et helt point: en nabo, der tænder byen
 * næsten lige så godt, slår den lange tværforbindelse. Før var det 2000 km.
 */
const SHORT_KM = 250;

/**
 * Det træk, der hjælper byen mest: størst andel af byens strøm bagefter, ingen andre byer i mørke, lidt
 * luft til næste årstid, et træk der kan gøres nu frem for et der skal spares op til, og ved lige gode
 * træk det billigste og nærmeste. null, når intet hjælper mærkbart.
 */
function bestFor(state: State, city: GridNode, options: Option[], base: State = flowed(state)): { o: Option; lift: Lift } | null {
  const was = nodeById(base, city.id);
  const now = was ? servedPct(was) : servedPct(city);
  let best: { o: Option; lift: Lift; score: number } | null = null;
  for (const o of options.slice(0, MAX_TRIES)) {
    const lift = liftOf(state, base, o.intent, city.id);
    if (!lift || lift.pct < now + 0.04) continue;
    const score = Math.min(lift.pct, 0.95) - 0.6 * lift.hurt + 0.15 * lift.slack + (o.affordable ? 0.15 : 0) - o.cost / 300 - o.d / SHORT_KM;
    if (!best || score > best.score) best = { o, lift, score };
  }
  return best;
}

/** Det valgte træk som råd: kan det betales nu, gives det; ellers spares der op til det. */
function pickAdvice(city: GridNode, p: { o: Option; lift: Lift }, urgent: boolean): Advice {
  // Til nærmeste 5 %, aldrig under 5: rådet hjælper mindst 4 point (bestFor), og "op på 0 %" lovede
  // ingenting (IGN runde 2, Viborg på bane 4).
  const partial = p.lift.pct * 1000 < SERVED_OK_PM ? Math.max(5, Math.round(p.lift.pct * 20) * 5) : undefined;
  if (p.o.affordable) return p.o.make(urgent, partial);
  const up = p.o.intent.kind === 'upgrade_line';
  return { kind: 'save', node: city.id, need: p.o.cost, what: up ? 'upgrade' : 'line', line: up ? (p.o.intent.target ?? null) : null, urgent };
}

interface Way {
  to: string;
  opt: LineMedium;
  cost: number;
  d: number;
  ok: boolean;
}

/** Nye ledninger fra `from` til noder, `accept` godtager: én pr. mål (luft før sø før kabel), nærmeste først. */
function routesFrom(state: State, from: string, accept: (o: GridNode) => boolean): Way[] {
  const p = nodeById(state, from);
  if (!p) return [];
  const out: Way[] = [];
  for (const o of state.nodes) {
    if (!o.active || o.phantom || o.kind === 'foreign' || o.id === from || !accept(o)) continue;
    for (const opt of ['air', 'sea', 'cable'] as LineMedium[]) {
      const r = check(state, 'build_line', pairKey(from, o.id), opt);
      const d = dist(p, o) + (opt === 'air' ? 0 : opt === 'sea' ? 5 : 20);
      if (r.ok) {
        out.push({ to: o.id, opt, cost: r.cost, d, ok: true });
        break;
      }
      if (r.error === 'too_poor') {
        out.push({ to: o.id, opt, cost: buildCost(state, opt, Math.round(dist(p, o) * 10) / 10), d, ok: false });
        break;
      }
      if (r.error === 'no_slots') break;
    }
  }
  return out.sort((a, b) => a.d - b.d);
}

function buildOption(from: string, w: Way, node: string, why: 'city' | 'power' | 'backup' | 'second'): Option {
  return {
    intent: { player: 'p1', clientSeq: 0, kind: 'build_line', target: pairKey(from, w.to), opt: w.opt, source: 'bot' },
    cost: w.cost,
    d: w.d,
    affordable: w.ok,
    make: (urgent, partial) => {
      const a: Advice = { kind: 'build', why, node, from, to: w.to, opt: w.opt, cost: w.cost, urgent };
      if (partial !== undefined) a.partial = partial;
      return a;
    },
  };
}

/** Fulde ledninger (over 90 %) i byens net, nærmeste byen først (i hop). */
export function fullLines(state: State, city: string): Array<{ line: Line; hops: number }> {
  const out: Array<{ line: Line; hops: number }> = [];
  const seen = new Set<string>([city]);
  const used = new Set<string>();
  let frontier = [city];
  let hops = 0;
  while (frontier.length > 0 && out.length < 6) {
    const next: string[] = [];
    for (const u of frontier) {
      for (const l of state.lines) {
        if (l.status !== 'ok' || used.has(l.id)) continue;
        const v = l.a === u ? l.b : l.b === u ? l.a : null;
        if (v === null) continue;
        used.add(l.id);
        if (l.loadPm >= 900 && !l.fixed && l.medium !== 'hvdc' && !heldLine(state, l.id)) out.push({ line: l, hops });
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
    }
    frontier = next;
    hops++;
  }
  return out;
}

/**
 * Kroner, der skal blive i kassen til ledninger ud til byer, der dukker op inden for 40 s: 10 kr pr.
 * by (puljens næste byer kommer efter kundeplanen).
 */
const CITY_RESERVE = 10;
const RESERVE_TICKS = 400;
export function cityReserve(state: State): number {
  let n = 0;
  state.pool.forEach((id, i) => {
    const at = i === 0 ? state.nextCustomerAt : (state.customerPlan[i] ?? Number.MAX_SAFE_INTEGER);
    if (at - state.clockTick <= RESERVE_TICKS && nodeById(state, id)?.kind === 'city') n++;
  });
  for (const s of state.pendingSites) {
    if (s.at - state.clockTick > RESERVE_TICKS) break;
    if (nodeById(state, s.node)?.kind === 'city') n++;
  }
  return Math.min(2, n) * CITY_RESERVE;
}

/** Den næste by, der dukker op (planlagt eller fra køen). */
function nextCity(state: State): string | null {
  // IGN-løkke 2, runde 3 (mobil): "Datacenter Ulstrup kostede 2 hjerter uden et eneste advarsels-hint".
  // Et datacenter er også en kunde, der kommer: rosen varsler det, når det er det næste.
  const wants = (n: GridNode | undefined): boolean => !!n?.city && (n.kind === 'city' || n.kind === 'datacenter');
  for (const s of state.pendingSites) {
    const n = nodeById(state, s.node);
    if (wants(n)) return s.node;
  }
  for (const id of state.pool) {
    if (wants(nodeById(state, id))) return id;
  }
  return null;
}

/**
 * Trænerens svar på et hændelseskort. Gratis svar er altid lovlige; et svar der koster, vælges kun
 * når der er penge til det og lidt til overs.
 */
export function cardChoice(state: State, t: Threat): CardOption {
  const can = (o: CardOption, spare = 4): boolean => (t.card?.options.includes(o) ?? false) && state.budget >= CARD_COST[o] + spare;
  switch (t.kind) {
    case 'protest': {
      if (can('cable', 12)) return 'cable';
      // Et borgermøde halverer ledningen i 20 s: godt, når den bærer under det halve lige nu.
      const l = lineById(state, t.targetLine);
      return l && Math.abs(l.flow) * 2 <= nominalCap(state, l) ? 'meeting' : 'build';
    }
    case 'spike':
      // Kan nettet bære hele bølgen, så hold den ud og få bonussen; ellers smart opladning.
      return rideSafe(state, t) || !can('smart') ? 'ride' : 'smart';
    case 'heatwave':
      return rideSafe(state, t) || !can('plan', 6) ? 'ride' : 'plan';
    case 'dc_rush':
      return can('reserve', 6) ? 'reserve' : 'wait';
    default:
      return t.card?.deflt ?? 'wait';
  }
}

/**
 * Kan nettet holde bølgen ud? Alle ramte byer har strøm og to veje, der er kraft nok til overs, og
 * ingen luftledning er over 70 % (hedebølgen tager en fjerdedel af dem).
 */
function rideSafe(state: State, t: Threat): boolean {
  let extra = 0;
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || n.phantom || c.demandMw <= 0) continue;
    const hit = t.kind === 'heatwave' || (t.nodes?.includes(n.id) ?? false);
    if (!hit) continue;
    if (c.servedMw < c.demandMw || n.shield < 2) return false;
    extra += (c.demandMw * (t.kind === 'heatwave' ? 10 : (t.pct ?? 30))) / 100;
  }
  let spare = 0;
  // Hedebølgen tager også af vinden og solen (spor "sværhed": før regnede træneren den med, som om
  // vinden blæste videre, og sagde "hold den ud" lige før en mangel på strøm).
  const keep = heatKeepPm(t.kind === 'heatwave' ? t.strength : 0);
  for (const n of state.nodes) {
    const p = n.producer;
    if (!n.active || !p) continue;
    spare += Math.max(0, p.availMw - p.usedMw);
    const k = n.kind === 'wind' ? keep.wind : n.kind === 'offshore' ? keep.sea : n.kind === 'solar' ? keep.sun : 1000;
    extra += (p.availMw * (1000 - k)) / 1000;
  }
  if (spare < extra * 1.3) return false;
  const airLimit = heatAirPm(t.kind === 'heatwave' ? t.strength : 0) - 50;
  if (t.kind === 'heatwave' && state.lines.some((l) => l.status === 'ok' && l.medium === 'air' && l.loadPm > airLimit)) return false;
  return true;
}

/** Den by hvis mørkeur er længst fremme (og hvor langt i promille af fuldt ur). */
function worstClock(state: State): { node: GridNode; pm: number } | null {
  const full = clockFull(state);
  let best: { node: GridNode; pm: number } | null = null;
  for (const n of state.nodes) {
    if (!n.city || !n.active || n.phantom || n.city.darkTicks > 0 || n.city.clock <= 0) continue;
    const pm = Math.round((n.city.clock * 1000) / full);
    if (!best || pm > best.pm) best = { node: n, pm };
  }
  return best;
}

/**
 * Power-up nu? Lynreserven, når en by er en tredjedel inde i sit mørkeur; Nødaflastning, når det er
 * ved at gå galt og Lynreserven er brugt: sluk den største by, der har det fint, så de andre får strøm.
 */
export function powerChoice(state: State): Extract<Advice, { kind: 'power' }> | null {
  const ok = (id: PowerId, target?: string): boolean => check(state, powerKind(id), target, undefined).ok;
  // Vindstille (bane 5): mangler en by strøm, ringes der til Norge med det samme, før uret når langt.
  if (dunkelStrength(state) > 0 && ok('norway')) {
    const short = state.nodes.find((n) => n.city && n.active && !n.phantom && n.city.demandMw > 0 && n.city.servedMw < n.city.demandMw);
    if (short) return { kind: 'power', power: 'norway', target: null, city: short.id, urgent: true };
  }
  // Værksnedbrud lige om lidt, og dykket vil koble en by fra: Lynreserven tager de første 500 MW.
  // Lynreserven varer 10 s, så den tændes først, når nedbruddet er under 6 s væk.
  const trip = tripSoon(state);
  if (trip && trip.nadir < UFLS_CHZ && trip.flywheelNode === null && trip.left <= 60 && !fcrActive(state) && ok('fcr')) {
    return { kind: 'power', power: 'fcr', target: null, city: trip.city ?? trip.plant, urgent: true };
  }
  const w = worstClock(state);
  if (!w || w.pm < 330) return null;
  // Norge gemmes, når en vindstille er varslet: dér gør vandkraften mest gavn.
  const dunkelSoon = state.threats.some((t) => t.kind === 'dunkelflaute' && t.phase === 'warning');
  if (!dunkelSoon && ok('norway')) return { kind: 'power', power: 'norway', target: null, city: w.node.id, urgent: true };
  if (ok('fcr')) return { kind: 'power', power: 'fcr', target: null, city: w.node.id, urgent: true };
  if (w.pm < 600 || !powerReady(state, 'shed')) return null;
  let shed: GridNode | null = null;
  for (const n of state.nodes) {
    if (n.id === w.node.id || !sheddable(n) || n.kind !== 'city' || !isPowered(n) || (n.city?.clock ?? 0) > 0) continue;
    if (!shed || (n.city?.demandMw ?? 0) > (shed.city?.demandMw ?? 0)) shed = n;
  }
  if (shed && ok('shed', shed.id)) return { kind: 'power', power: 'shed', target: shed.id, city: w.node.id, urgent: true };
  return null;
}

/** Et værksnedbrud på vej: hvor dybt dykket bliver, og hvor et svinghjul kan stå (null: ingen plads eller råd). */
export interface TripSoon {
  plant: string;
  area: Area;
  nadir: number;
  /** Ticks til nedbruddet. */
  left: number;
  /** Den største by i prisområdet, den nettet ville koble fra. */
  city: string | null;
  flywheelNode: string | null;
  cost: number;
}

export function tripSoon(state: State): TripSoon | null {
  for (const t of state.threats) {
    if (t.kind !== 'plant_trip' || t.phase !== 'warning' || !t.area || !t.dP) continue;
    const plant = t.nodes?.[0] ?? '';
    const area = t.area;
    const nadir = predictNadir(state, area, t.dP, plant);
    let city: GridNode | null = null;
    for (const n of state.nodes) {
      if (n.area !== area || !n.city || !n.active || n.phantom || n.kind !== 'city') continue;
      if (!city || n.city.demandMw > (city.city?.demandMw ?? 0)) city = n;
    }
    // Svinghjulet står helst ved en station (det er dér, synkronkompensatorer står), ellers ved den største by.
    const spots = state.nodes
      .filter((n) => n.area === area && n.active && !n.phantom && n.flywheels < 2 && (n.kind === 'station' || n.kind === 'city'))
      .sort((a, b) => (a.kind === 'station' ? 0 : 1) - (b.kind === 'station' ? 0 : 1) || a.flywheels - b.flywheels || a.id.localeCompare(b.id));
    let flywheelNode: string | null = null;
    let cost = 0;
    for (const n of spots) {
      const c = check(state, 'build_flywheel', n.id, undefined);
      if (c.ok) {
        flywheelNode = n.id;
        cost = c.cost;
        break;
      }
    }
    return { plant, area, nadir, left: t.hitAt - state.clockTick, city: city?.id ?? null, flywheelNode, cost };
  }
  return null;
}

/** Det vigtigste råd lige nu, eller null når der ikke er noget at sige. */
export function advise(state: State): Advice | null {
  return adviseFrom(state, false, true);
}

/** Trænerens hjælp på denne sværhed: bane 1 år 0 får altid det hele, så alle lærer spillet. */
export function coachModeOf(state: State): CoachMode {
  if (levelOf(state).number === 1 && state.year === 0) return 'full';
  const d = difficulty(state.difficulty);
  if (state.year === 0 && !state.finale && d.firstYearCoach) return d.firstYearCoach;
  // Hånden slipper kortet over byggeriet, ikke hændelseskortet: et åbent kort er en beslutning med uret
  // stoppet, og Lærlings forslag på kortet lærer, hvad svarene koster (IGN-løkke 2).
  if (d.coachFade && state.year > 0 && levelOf(state).number >= d.coachFade.fromLevel && !state.threats.some(cardOpen)) return d.coachFade.mode;
  return d.coach;
}

/**
 * Det råd, UI'et skal vise på denne sværhed, og hvordan. full: alle råd med hånd. urgent: kun de
 * akutte, med hånd. ring: kun de akutte, som en ring om målet. off: intet. Er det vigtigste råd ikke
 * akut, ledes der videre efter det vigtigste akutte.
 */
/**
 * `role`: makkerspillets rolle (co-op-anmelderen, IGN-løkke runde 3: "begge får det samme hint").
 * Planlæggeren får byggerådene og springer storm, brud, gæster og kort over; dem tager Driftsvagten,
 * som ellers får det samme råd som alle andre.
 */
export function adviseFor(state: State, role?: 'build' | 'ops'): (Advice & { display: 'hand' | 'ring' }) | null {
  const mode = coachModeOf(state);
  if (mode === 'off') return null;
  const a = adviseFrom(state, mode !== 'full', mode !== 'ring', role !== 'build', role === 'ops');
  if (!a) return null;
  return { ...a, display: mode === 'ring' ? 'ring' : 'hand' };
}

function adviseFrom(state: State, urgentOnly: boolean, tips: boolean, ops = true, opsOnly = false): Advice | null {
  if (state.phase !== 'playing') return null;
  const take = (a: Advice | null): Advice | null => (a && (!urgentOnly || a.urgent) ? a : null);
  const plantOk = plantAdvisable(state);

  // 1. Brud, og ingen montør ledig: hyr en mere. (Ledige montører kører selv.)
  const broken = state.lines.find((l) => (l.status === 'broken' || l.halfDown) && !state.crews.some((c) => c.task !== 'idle' && c.lineId === l.id));
  // Står der en ledig montør (fx lige hyret), kører den selv: så er rådet ikke "hyr en mere" (IGN runde 2).
  const idleVan = state.crews.some((c) => c.kind === 'van' && c.task === 'idle');
  if (ops && broken && crewKindFor(broken) === 'van' && !idleVan) {
    const c = check(state, 'hire_crew', undefined, undefined);
    if (c.ok) return { kind: 'hire', line: broken.id, cost: c.cost, urgent: true };
  }
  // 2. Storm på vej: forstærk en ledning i sporet. Men ikke for de sidste kroner, når en ny by er på
  //    vej: et brud kan en montør lappe, en by uden ledning går i sort.
  const keep = cityReserve(state);
  // 1b. En ny by uden ledning går foran stormen og gæsterne. IGN-løkke 2: "hintet sagde forstærk,
  //     mens en ny by stod og ventede". En storm kan knække en ledning, som en montør lapper; en by uden
  //     ledning går altid i sort. Kun hvis ingen by med ledning er tættere på mørke (trin 3d).
  const lvl = levelOf(state);
  const darkIn = (n: GridNode): number => darkInTicks(state, lvl, n) ?? Number.MAX_SAFE_INTEGER;
  const critical = state.nodes.some((n) => wanting(n) && hasAnyLine(state, n.id) && darkIn(n) <= CRITICAL_TICKS);
  // Driftsvagten (makkerspil) får stormen først: byen er Planlæggerens. Står der også et hændelseskort
  // åbent, går kortet forrest: det er ét tryk og lukker om få sekunder, byen har sin frist (playtest:
  // Lærlings kortforslag forsvandt bag den nye by).
  if (!critical && !opsOnly) {
    const lonely = state.nodes
      .filter((n) => n.city && (n.kind === 'city' || n.kind === 'datacenter') && n.active && !n.phantom && !hasAnyLine(state, n.id))
      .sort((a, b) => darkIn(a) - darkIn(b));
    const openCard = ops && lonely.length > 0 ? state.threats.find(cardOpen) : undefined;
    if (openCard) return { kind: 'card', threat: openCard.id, opt: cardChoice(state, openCard), urgent: true };
    for (const n of lonely) {
      const a = take(connect(state, n, 'city', true, plantOk));
      if (a) return a;
    }
  }
  // IGN-løkke runde 2: ét tryk forstærker alle udsatte master. Er der ikke råd til alle, peges på én.
  const exposed = ops ? exposedLines(state) : [];
  if (exposed.length > 1) {
    const c = check(state, 'reinforce_mast', REINFORCE_ALL, undefined);
    if (c.ok && state.budget - c.cost >= keep) return { kind: 'reinforce', line: REINFORCE_ALL, count: exposed.length, cost: c.cost, urgent: true };
  }
  for (const l of exposed) {
    const c = check(state, 'reinforce_mast', l.id, undefined);
    if (c.ok && state.budget - c.cost >= keep) return { kind: 'reinforce', line: l.id, count: 1, cost: c.cost, urgent: true };
  }
  // 3. En gravko eller ankerbåd, man kan jage væk. Småt-gæster (stork, ko) venter, til byerne har
  //    det godt: de er det mindste tryk i spillet (playtest runde 3: "Tryk på gæsten" mens en by gik ud).
  const guest = ops && state.threats.find((t) => t.kind !== 'smaa' && check(state, 'shoo_threat', t.id, undefined).ok);
  if (guest) return { kind: 'shoo', threat: guest.id, urgent: true };
  // 3b. Et hændelseskort venter på svar.
  const card = ops && state.threats.find(cardOpen);
  if (card) return { kind: 'card', threat: card.id, opt: cardChoice(state, card), urgent: true };
  // 3b2. Et værk er ved at falde ud, og dykket vil koble en by fra: et svinghjul i prisområdet tager det.
  const trip = ops ? tripSoon(state) : null;
  if (trip && trip.nadir < UFLS_CHZ && trip.flywheelNode) return { kind: 'flywheel', node: trip.flywheelNode, cost: trip.cost, nadir: trip.nadir, urgent: true };
  // 3c. En by er ved at gå mørk: en power-up kan købe tid. Har byen slet ingen ledning, er ledningen
  //     svaret, og Lynreserven kun en nødhjælp (playtest runde 3: "Tryk på Lynreserve" for en ny by).
  const pw = ops ? powerChoice(state) : null;
  if (pw) {
    const c = nodeById(state, pw.city);
    if (c && !hasAnyLine(state, c.id)) {
      const a = connect(state, c, 'city', true, plantOk);
      if (a && a.kind === 'build') return a;
    }
    return pw;
  }

  // En knækket ledning, som en montør er på vej til: sig det, før der foreslås kul eller omveje.
  const waiting = state.lines.find((l) => l.status === 'broken' && state.crews.some((c) => c.task !== 'idle' && c.lineId === l.id));
  const darkSoon = state.nodes.some(wanting);

  // 3d. Den by, der er nærmest at gå i sort, går foran en ny by, der stadig har frist (IGN runde 1:
  //     "træneren roste den største by, mens en anden var 6 s fra mørke").
  const level = levelOf(state);
  const left = (n: GridNode): number => darkInTicks(state, level, n) ?? Number.MAX_SAFE_INTEGER;
  const short = state.nodes.filter(wanting).sort((a, b) => left(a) - left(b) || (b.city?.clock ?? 0) - (a.city?.clock ?? 0));
  const first = short[0];
  if (first && hasAnyLine(state, first.id) && left(first) <= CRITICAL_TICKS) {
    const lone = state.nodes.filter((n) => n.city && (n.kind === 'city' || n.kind === 'datacenter') && n.active && !n.phantom && !hasAnyLine(state, n.id));
    if (lone.every((n) => left(n) > left(first))) {
      const a = take(shortAdvice(state, first, plantOk));
      if (a && a.kind !== 'calm') return a;
    }
  }

  // 4. En by uden ledning.
  for (const n of state.nodes) {
    if (!n.city || (n.kind !== 'city' && n.kind !== 'datacenter') || !n.active || n.phantom || hasAnyLine(state, n.id)) continue;
    // Altid akut: byggetid plus et kort ur gør et råd, der først kommer, når uret løber, for sent
    // (playtest runde 2: Kolding gik i sort, før den foreslåede ledning var bygget).
    const a = take(connect(state, n, 'city', true, plantOk));
    if (a) return a;
  }

  // 5. En by der mangler strøm: er der ledig produktion i nettet, er det ledningen, der er flaskehalsen;
  //    ellers mangler der produktion.
  if (ops && waiting && darkSoon) {
    const count = state.lines.filter((l) => l.status === 'broken').length;
    return { kind: 'broken', line: waiting.id, count, urgent: true };
  }
  for (const city of short) {
    const a = take(shortAdvice(state, city, plantOk));
    if (a) return a;
  }
  // Ingen træk hjælper den mørkeste by lige nu: det siges til sidst, hvis heller ikke de rolige trin har noget.
  const lack: Advice | null = short[0] ? { kind: 'calm', node: short[0].id, urgent: false, lack: true } : null;
  // 5b. Byerne har det godt nok: nu må en småt-gæst gerne jages.
  const small = ops && state.threats.find((t) => t.kind === 'smaa' && check(state, 'shoo_threat', t.id, undefined).ok);
  if (small) return { kind: 'shoo', threat: small.id, urgent: true };

  // 6. En producent uden ledning, den største først (Thor før en lille mølle). Et værk kun, når vind og
  //    sol ikke rækker. Kan en stor producent ikke kobles på endnu, siges hvad der mangler.
  const lone = state.nodes
    .filter((n) => n.producer && n.active && !n.phantom && n.kind !== 'foreign' && !hasAnyLine(state, n.id))
    // Et værk kobles kun på for at redde en by (trin 5), aldrig som et roligt råd: så falder den
    // grønne andel uden grund (IGN runde 3, bane 1).
    .filter((n) => !isPlant(n))
    .sort((a, b) => (b.producer?.capMw ?? 0) - (a.producer?.capMw ?? 0));
  // Akut-træneren (Vagthavende) tier ellers, men et roligt tip om vej nr. 2 eller et batteri må den
  // gerne give: det er dér, pengene skal hen (playtest runde 2: "pengene hober sig op"). Ringen
  // (Systemansvarlig) tier helt.
  if (urgentOnly) return tips ? quietTip(state, keep) : null;
  for (const n of lone) {
    const a = connect(state, n, 'producer', false, plantOk);
    if (a && (a.kind === 'build' || (n.producer?.capMw ?? 0) >= BIG_PRODUCER_MW)) return a;
  }

  // 7. En ledning der er ved at være fuld, og som kan opgraderes nu.
  for (const l of state.lines) {
    if (l.status !== 'ok' || l.fixed || l.loadPm < 950) continue;
    const c = check(state, 'upgrade_line', l.id, undefined);
    if (c.ok && state.budget - c.cost >= keep) return { kind: 'upgrade', line: l.id, node: null, cost: c.cost, urgent: false };
  }

  // 8. Rolig periode og penge til overs: vej nr. 2 til en by med én vej ind, eller et batteri.
  const tip = quietTip(state, keep);
  if (tip) return tip;

  if (lack) return lack;

  // 9. Alt kører: ros, og fortæl hvad der kommer. Men aldrig mens en by sidder i mørke (IGN runde 1).
  const dark = state.nodes.find((n) => n.city && n.active && !n.phantom && n.city.darkTicks > 0);
  if (dark) return { kind: 'praise', node: dark.id, next: null, urgent: false, dark: true };
  // Den nyeste by med strøm, ikke altid den største: rosen handler om det, spilleren lige har gjort.
  let big: GridNode | null = null;
  for (const n of state.nodes) {
    if (!n.city || n.kind !== 'city' || !n.active || n.phantom || !isPowered(n) || n.city.servedMw <= 0) continue;
    if (!big || n.city.grewAt > (big.city?.grewAt ?? 0) || (n.city.grewAt === big.city?.grewAt && n.city.demandMw > (big.city?.demandMw ?? 0))) big = n;
  }
  if (big) return { kind: 'praise', node: big.id, next: nextCity(state), urgent: false };
  return null;
}

/** Så tæt på mørke (2 s til et fuldt ur på de fleste niveauer) går byen foran alt andet end akutte farer. */
const CRITICAL_TICKS = 80;

/**
 * Rådet for én by, til bykortet, når spilleren trykker på den (IGN runde 1: "hvad mangler den?"). Uden
 * ledning: den første. Mangler den strøm: det bedste træk. Har den strøm, men kun én vej ind: vej nr. 2.
 */
export function cityAdvice(state: State, id: string): Advice | null {
  if (state.phase !== 'playing') return null;
  const n = nodeById(state, id);
  if (!n?.city || !n.active || n.phantom || n.kind === 'foreign') return null;
  const plantOk = plantAdvisable(state);
  if (!hasAnyLine(state, id)) return connect(state, n, 'city', true, plantOk);
  if (n.city.darkTicks > 0) return null;
  if (wanting(n)) return shortAdvice(state, n, plantOk) ?? { kind: 'calm', node: id, urgent: false, lack: true };
  if (n.shield === 1) {
    const r = secondRoute(state, n, netOf(state, id));
    if (r?.ok) return { kind: 'build', why: 'second', node: id, from: r.from, to: r.to, opt: r.opt, cost: r.cost, urgent: false };
  }
  return null;
}

function shortAdvice(state: State, city: GridNode, plantOk: boolean): Advice | null {
  // Får byen strøm, så snart flowet er regnet igen (en ny ledning er lige bygget), er svaret at vente.
  const base = flowed(state);
  const fresh = nodeById(base, city.id);
  if (fresh && isPowered(fresh)) return { kind: 'calm', node: city.id, urgent: false, soon: true };
  const net = netOf(state, city.id);
  // Øvelsesåret: urene står stille, så kul er aldrig svaret.
  const frozen = clocksFrozen(state, levelOf(state));
  const options: Option[] = [];
  // a) Fulde ledninger i byens eget net, nærmeste byen først. Hver prøves af, så hintet aldrig peger på
  //    en ledning langt væk, der ikke redder byen (playtest runde 3: opgradér-hint reddede ikke Holstebro).
  for (const { line, hops } of fullLines(state, city.id)) {
    const cost = upgradeCost(state, line);
    if (cost === null) continue;
    const c = check(state, 'upgrade_line', line.id, undefined);
    if (!c.ok && c.error !== 'too_poor') continue;
    options.push({
      intent: { player: 'p1', clientSeq: 0, kind: 'upgrade_line', target: line.id, source: 'bot' },
      cost,
      d: hops * 10,
      affordable: c.ok,
      make: (urgent, partial) => {
        const a: Advice = { kind: 'upgrade', line: line.id, node: city.id, cost, urgent };
        if (partial !== undefined) a.partial = partial;
        return a;
      },
    });
  }
  // b) En ledig producent uden for nettet: ind til nettet, eller lige ind i byen (grøn først).
  const producers = state.nodes
    .filter((n) => n.producer && n.active && !n.phantom && n.kind !== 'foreign' && !net.has(n.id) && n.producer.availMw - n.producer.usedMw >= 10)
    .filter((n) => (plantOk && !frozen) || !isPlant(n))
    .sort((a, b) => Number(isPlant(a)) - Number(isPlant(b)) || dist(a, city) - dist(b, city))
    .slice(0, 3);
  for (const p of producers) {
    const why = isPlant(p) ? 'backup' : 'power';
    const into = routesFrom(state, p.id, (o) => net.has(o.id))[0];
    const coal = why === "backup";
    if (into) options.push({ ...buildOption(p.id, into, city.id, why), coal });
    const direct = into?.to === city.id ? undefined : routesFrom(state, p.id, (o) => o.id === city.id)[0];
    if (direct) options.push({ ...buildOption(p.id, direct, city.id, why), coal });
  }
  // c) En vej mere ind i byen fra nettet eller fra en grøn producent.
  const near = new Set(state.lines.filter((l) => l.a === city.id || l.b === city.id).map((l) => (l.a === city.id ? l.b : l.a)));
  const second = routesFrom(state, city.id, (o) => !near.has(o.id) && ((net.has(o.id) && degree(state, o.id) > 0) || (!!o.producer && !isPlant(o)))).slice(0, 3);
  for (const w of second) options.push(buildOption(city.id, w, city.id, 'second'));
  // Det, der kan gøres nu, og det billigste først, så de bedste kandidater altid bliver prøvet.
  options.sort((a, b) => Number(b.affordable) - Number(a.affordable) || a.cost + a.d / 4 - (b.cost + b.d / 4));
  // Kul er sidste udvej (IGN runde 1: "hintet skubber kul"): et værk foreslås kun, når intet grønt træk
  // får byen op over grænsen.
  const green = bestFor(state, city, options.filter((o) => !o.coal), base);
  const rescues = (p: { lift: Lift } | null): boolean => !!p && p.lift.pct * 1000 >= SERVED_OK_PM;
  const pick = rescues(green) || !options.some((o) => o.coal) ? green : (bestFor(state, city, options, base) ?? green);
  if (pick) return pickAdvice(city, pick, true);
  // Intet træk hjælper. Mangler byen en plads, er en station svaret; ellers siges det ærligt.
  const r = secondRoute(state, city, net);
  if (r && !r.ok) {
    const b = blocked(state, r, city.id, true);
    if (b?.kind === 'station' || (b?.kind === 'save' && b.what === 'station')) return b;
  }
  if (frozen) return { kind: 'calm', node: city.id, urgent: false };
  // null: de rolige trin (en ny producent, en opgradering, vej nr. 2) får lov at prøve først, og
  // først når intet af dem har noget, siger adviseFrom det ærligt (lack).
  return null;
}

/** En ledning mere ind i byen: til nettet eller en grøn producent, ikke til en nabo den har i forvejen. */
function secondRoute(state: State, city: GridNode, net: Set<string>): Route | null {
  const near = new Set(state.lines.filter((l) => l.a === city.id || l.b === city.id).map((l) => (l.a === city.id ? l.b : l.a)));
  return routeFrom(state, city.id, (o) => !near.has(o.id) && ((net.has(o.id) && degree(state, o.id) > 0) || (!!o.producer && !isPlant(o))));
}

/** Er noden en kunde fra banens kø? Så koster den den tredje stjerne, hvis den aldrig får en ledning. */
export function starCustomer(state: State, id: string): boolean {
  const n = nodeById(state, id);
  if (!n || n.kind === 'station') return false;
  return levelOf(state).sites.some((s) => s.node === id && s.at === 'pool');
}

/**
 * Den fulde ledning, der holder mest grøn strøm tilbage: en ledning ved en vind- eller solpark med
 * mindst 30 MW til overs, der kører over 90 % og kan opgraderes.
 */
function curtailedLine(state: State): { line: string; cost: number } | null {
  let best: { line: string; cost: number; spare: number } | null = null;
  for (const n of state.nodes) {
    if (!n.producer || !n.active || n.phantom || isPlant(n) || n.kind === 'foreign') continue;
    const spare = n.producer.availMw - n.producer.usedMw;
    if (spare < 30) continue;
    for (const l of state.lines) {
      if ((l.a !== n.id && l.b !== n.id) || l.status !== 'ok' || l.fixed || l.loadPm < 900) continue;
      const c = check(state, 'upgrade_line', l.id, undefined);
      if (!c.ok) continue;
      if (!best || spare > best.spare) best = { line: l.id, cost: c.cost, spare };
    }
  }
  return best ? { line: best.line, cost: best.cost } : null;
}

/** Skal byen have to veje ind for den tredje stjerne på denne sværhed? */
function needsTwo(state: State, n: GridNode): boolean {
  const n1 = difficulty(state.difficulty).n1;
  if (n1 === 'required' || n1 === 'cascades') return true;
  return (n.city?.tier ?? 0) >= STAR_N1_TIER;
}

/**
 * Et roligt tip, når der er penge til overs: et gratis batteri, der venter; ellers vej nr. 2 til den
 * største by med én vej ind (de byer, stjernen kræver, først); ellers et batteri ved den største by,
 * når vind og sol ikke rækker. Aldrig i øvelsesåret på bane 1, og aldrig for de sidste kroner.
 */
function quietTip(state: State, keep: number): Advice | null {
  if (levelOf(state).number === 1 && state.year === 0) return null;
  if (state.threats.some((t) => t.phase === 'active' || (t.kind === 'storm' && t.phase === 'warning'))) return null;
  // Et gratis stort batteri fra årsskiftet: sig, hvor det gør mest gavn.
  if (state.perks.freeBattery > 0) {
    const at = batterySpot(state);
    if (at) return { kind: 'battery', node: at.id, cost: 0, urgent: false };
  }
  // En grøn producent uden ledning, mens den grønne andel er under målet: det er stjernen, der ligger
  // der (playtest runde 3: Blåvand stod ubrugt hele banen, og ingen sagde det).
  // IGN runde 3: også når målet er nået, for en kunde fra køen uden ledning koster den tredje stjerne
  // (Videbæk Vind stod alene, og træneren sagde intet).
  const below = greenPct(state) < greenGoal(levelOf(state).greenGoalPct, state.difficulty);
  const lone = state.nodes
    .filter((n) => n.producer && n.active && !n.phantom && n.kind !== 'foreign' && !isPlant(n) && !hasAnyLine(state, n.id))
    .filter((n) => below || starCustomer(state, n.id))
    .sort((a, b) => (b.producer?.capMw ?? 0) - (a.producer?.capMw ?? 0));
  for (const n of lone) {
    const r = routeFrom(state, n.id, (o) => degree(state, o.id) > 0);
    if (r?.ok && state.budget - r.cost >= keep) return { kind: 'build', why: 'producer', node: n.id, from: r.from, to: r.to, opt: r.opt, cost: r.cost, urgent: false };
  }
  // Under det grønne mål, og en mølle står og venter på plads i en fuld ledning: opgradér den
  // (IGN runde 3: hintene på bane 3 gav 56 % mod et mål på 64 %).
  if (below) {
    const up = curtailedLine(state);
    if (up && state.budget - up.cost >= keep) return { kind: 'upgrade', line: up.line, node: null, cost: up.cost, urgent: false, green: true };
  }
  const singles = state.nodes
    .filter((n) => n.city && n.kind === 'city' && n.active && !n.phantom && n.shield === 1 && isPowered(n))
    .sort((a, b) => Number(needsTwo(state, b)) - Number(needsTwo(state, a)) || (b.city?.demandMw ?? 0) - (a.city?.demandMw ?? 0));
  for (const n of singles) {
    const r = secondRoute(state, n, netOf(state, n.id));
    if (r?.ok && state.budget - r.cost >= keep + 5) return { kind: 'build', why: 'second', node: n.id, from: r.from, to: r.to, opt: r.opt, cost: r.cost, urgent: false };
  }
  if (!greenEnough(state)) {
    const at = batterySpot(state);
    const c = at ? check(state, 'build_battery', at.id, undefined) : null;
    if (at && c?.ok && state.budget - c.cost >= keep + 10) return { kind: 'battery', node: at.id, cost: c.cost, urgent: false };
  }
  return null;
}

/** Den største by med strøm og uden batteri, hvor et batteri kan bygges. */
function batterySpot(state: State): GridNode | null {
  let best: GridNode | null = null;
  for (const n of state.nodes) {
    if (!n.city || n.kind !== 'city' || !n.active || n.phantom || n.battery || !isPowered(n)) continue;
    const c = check(state, 'build_battery', n.id, undefined);
    if (!c.ok && c.error !== 'too_poor') continue;
    if (!best || n.city.demandMw > (best.city?.demandMw ?? 0)) best = n;
  }
  return best;
}

/** Rådet som en handling, når det kan gøres med ét tryk (eller null). Bruges af botter og målinger. */
export function adviceIntent(state: State, a: Advice, player: PlayerId = 'p1'): Intent | null {
  const act = (kind: Intent['kind'], target: string | undefined, opt?: string): Intent | null => {
    if (!check(state, kind, target, opt, player).ok) return null;
    const i: Intent = { player, clientSeq: 0, kind, source: 'bot' };
    if (target !== undefined) i.target = target;
    if (opt !== undefined) i.opt = opt;
    return i;
  };
  switch (a.kind) {
    case 'hire':
      return act('hire_crew', undefined);
    case 'reinforce':
      return act('reinforce_mast', a.line);
    case 'broken':
      return null;
    case 'shoo':
      return act('shoo_threat', a.threat);
    case 'card':
      return act('answer_card', a.threat, a.opt);
    case 'power':
      return act(powerKind(a.power), a.target ?? undefined);
    case 'upgrade':
      return act('upgrade_line', a.line);
    case 'build':
      return act('build_line', `${a.from}|${a.to}`, a.opt);
    case 'station':
      return act('upgrade_station', a.node);
    case 'battery':
      return act('build_battery', a.node);
    case 'flywheel':
      return act('build_flywheel', a.node);
    case 'save':
    case 'calm':
    case 'praise':
      return null;
  }
}

/** Den handling, rådet beder om (til rollerne i makkerspillet). null: rådet er ikke en handling. */
export function adviceKind(a: Advice): ActionKind | null {
  switch (a.kind) {
    case 'hire':
      return 'hire_crew';
    case 'reinforce':
      return 'reinforce_mast';
    case 'shoo':
      return 'shoo_threat';
    case 'card':
      return 'answer_card';
    case 'power':
      return powerKind(a.power);
    case 'build':
      return 'build_line';
    case 'upgrade':
      return 'upgrade_line';
    case 'station':
      return 'upgrade_station';
    case 'battery':
      return 'build_battery';
    case 'flywheel':
      return 'build_flywheel';
    default:
      return null;
  }
}

/**
 * Driftsvagtens eget råd, når holdets vigtigste råd er Planlæggerens (IGN-løkke runde 4): en småt-gæst,
 * der kan jages. Opgradering er Planlæggerens siden spor coop. Ellers null.
 */
export function opsTip(state: State): Advice | null {
  if (state.phase !== 'playing') return null;
  const small = state.threats.find((t) => check(state, 'shoo_threat', t.id, undefined).ok);
  if (small) return { kind: 'shoo', threat: small.id, urgent: true };
  return null;
}

/** Hvor hånden skal trække: altid de samme to noder, som teksten nævner. */
export function adviceDrag(a: Advice): { from: string; to: string } | null {
  return a.kind === 'build' ? { from: a.from, to: a.to } : null;
}

/** Trænerens begrundelse for svaret på et kort. */
const CARD_HINT: Record<CardOption, string> = {
  cable: 'Grav et kabelstykke ned, så er naboerne glade.',
  meeting: 'Hold borgermøde. Ledningen kører halvt imens.',
  build: 'Byg alligevel, hvis du ikke har råd til andet.',
  smart: 'Smart opladning tager toppen af bølgen.',
  ride: 'Hold den ud. Lynreserven kan hjælpe.',
  plan: 'Planlæg kølingen, så byerne ikke bruger mere.',
  reserve: 'Reservér kapacitet. Så afslører du fantomerne.',
  wait: 'Vent og se. Fantomerne forsvinder af sig selv.',
};

/** Gæsten i bestemt form, så "tryk på" peger på noget, man kan se (playtest runde 3: "hvilken gæst?"). */
const GUEST: Record<string, [string, string]> = {
  digger: ['gravkoen', 'den'],
  anchor: ['ankerbåden', 'den'],
  stork: ['storken', 'den'],
  drage: ['dragen', 'den'],
  ko: ['koen', 'den'],
  juletrae: ['juletræet', 'det'],
};

function guestOf(state: State, id: string): [string, string] {
  const t = state.threats.find((x) => x.id === id);
  const key = t?.kind === 'smaa' ? (t.variant ?? '') : (t?.kind ?? '');
  return GUEST[key] ?? ['gæsten', 'den'];
}

export function guestName(state: State, id: string): string {
  return guestOf(state, id)[0];
}

function guestPronoun(state: State, id: string): string {
  return guestOf(state, id)[1];
}

/** Mange penge i kassen: sig det, før et roligt råd (IGN runde 3: "Du har 150 kr: …"). */
const RICH_KR = 60;
function rich(state: State): string {
  // Med en makker er kassen fælles (co-op runde 2: Driftsvagten fik "Du har 85 kr" om Volts penge).
  return state.budget >= RICH_KR ? `${state.coop || state.roles ? 'I' : 'Du'} har ${Math.floor(state.budget)} kr. ` : '';
}

/** Kort, varm tekst til rådet. */
export function adviceText(state: State, a: Advice, touch: boolean): string {
  return rawText(state, a, touch).trim();
}

function rawText(state: State, a: Advice, touch: boolean): string {
  const base = baseText(state, a, touch);
  // Hjælper rådet kun et stykke, siges det: så ved man, at der skal mere til (playtest runde 3).
  if (a.kind === 'build' && a.partial !== undefined && a.why !== 'city') return `${base} Så får den ${a.partial} %, og der skal mere til.`;
  return base;
}

function baseText(state: State, a: Advice, touch: boolean): string {
  const name = (id: string): string => nodeById(state, id)?.name ?? '';
  const drag = touch ? 'Træk' : 'Træk';
  const line = (id: string, from: string | null): string => {
    const l = state.lines.find((x) => x.id === id);
    if (!l) return 'ledningen';
    if (from && (l.a === from || l.b === from)) return `ledningen fra ${name(l.a === from ? l.b : l.a)}`;
    return `ledningen mellem ${name(l.a)} og ${name(l.b)}`;
  };
  // Aldrig MW i UI'et: andelen af byens strøm i hele procent, rundet ned, så en by i nød aldrig får 100 %.
  const gets = (id: string): string => {
    const c = nodeById(state, id)?.city;
    if (!c || c.demandMw <= 0) return '';
    // Rundet ned til hele tiere: et tal, der tæller ned hvert sekund, er støj (playtest runde 3).
    const pct = Math.min(90, Math.floor((c.servedMw * 10) / c.demandMw) * 10);
    return `${name(id)} får kun ${pct} % af sin strøm.`;
  };
  switch (a.kind) {
    case 'hire':
      return `Alle montører er optaget. Hyr en mere ${forKr(a.cost)}.`;
    case 'reinforce': {
      // På de milde niveauer koster et brud penge: sig prisen, så valget er til at se.
      const bill = difficulty(state.difficulty).repairKr ?? 0;
      if (a.count > 1)
        return bill > 0
          ? `Storm på vej mod ${a.count} master. Forstærk alle udsatte ${forKr(a.cost)}, ellers ${bill} kr pr. brud.`
          : `Storm på vej mod ${a.count} master. Forstærk alle udsatte ${forKr(a.cost)}.`;
      return bill > 0
        ? `Storm på vej. Tryk på ${line(a.line, null)} og forstærk masterne: ${REINFORCE_COST} kr nu, ellers ${bill} kr i reparation.`
        : `Storm på vej. Tryk på ${line(a.line, null)} og forstærk masterne.`;
    }
    case 'shoo':
      return `Tryk på ${guestName(state, a.threat)} for at jage ${guestPronoun(state, a.threat)} væk.`;
    case 'card':
      return `Et kort venter på dit svar. ${CARD_HINT[a.opt]}`;
    case 'power':
      if (a.power === 'fcr' && tripSoon(state)) return `Et værk ryger om lidt. Tryk på Lynreserve: den tager de første 500 MW, så frekvensen holder.`;
      if (a.power === 'fcr') {
        const lone = !hasAnyLine(state, a.city);
        return lone
          ? `${name(a.city)} har ingen ledning og er ved at gå mørk. Lynreserve giver den 5 s ekstra til at nå det.`
          : `${name(a.city)} er ved at gå mørk. Tryk på Lynreserve: mørke byer får 5 s ekstra.`;
      }
      if (a.power === 'norway' && dunkelStrength(state) > 0) return `Vinden sover, og ${name(a.city)} mangler strøm. Ring til Norge: vandkraft lander i Tjele i 20 s.`;
      if (a.power === 'norway') return `${name(a.city)} er ved at gå mørk. Ring til Norge: vandkraft hjælper i 20 s.`;
      return `${name(a.city)} er ved at gå mørk. Nødaflastning: sluk ${name(a.target ?? '')} i 10 s, så ${name(a.city)} får strømmen.`;
    case 'broken':
      if (a.count > 1) return `${a.count} ledninger er knækket. Montørerne kører i rækkefølge, den første er på vej.`;
      return `${line(a.line, null).replace(/^l/, 'L')} er knækket. Montøren er på vej.`;
    case 'battery':
      if (a.cost === 0) return `Dit store batteri venter. Tryk på ${name(a.node)} og byg det, så klarer byen toppen.`;
      return `${rich(state)}Vind og sol rækker ikke altid. Tryk på ${name(a.node)} og byg et batteri ${forKr(a.cost)}. Det tager toppen.`;
    case 'flywheel':
      return `Et værk ryger snart, og frekvensen falder til ${hz(a.nadir)}. Tryk på ${name(a.node)} og byg et svinghjul ${forKr(a.cost)}, så ingen by kobles fra.`;
    case 'build':
      switch (a.why) {
        case 'city':
          if (a.partial !== undefined) return `${drag} en ledning fra ${name(a.from)} til ${name(a.to)}. Den giver ${a.partial} % af strømmen, så byg også en vej mere ind bagefter.`;
          return `${drag} en ledning fra ${name(a.from)} til ${name(a.to)}.`;
        case 'producer':
          if (!a.urgent && nodeById(state, a.node)?.producer && greenPct(state) < greenGoal(levelOf(state).greenGoalPct, state.difficulty))
            return `${rich(state)}${name(a.node)} står ubrugt. Grøn strøm giver stjernen: træk en ledning fra ${name(a.from)} til ${name(a.to)}, ${kr(a.cost)}.`;
          if (!a.urgent && starCustomer(state, a.node))
            return `${rich(state)}${name(a.node)} venter på en ledning, ellers ryger den tredje stjerne. Træk en fra ${name(a.from)} til ${name(a.to)}, ${kr(a.cost)}.`;
          return `${drag} en ledning fra ${name(a.from)} til ${name(a.to)}.`;
        case 'power':
          return `${gets(a.node)} ${drag} en ledning fra ${name(a.from)} til ${name(a.to)}.`;
        case 'backup':
          return `${gets(a.node)} Vind og sol rækker ikke lige nu. ${drag} en ledning fra ${name(a.from)} til ${name(a.to)} som reserve.`;
        case 'second':
          if (a.urgent) return `${gets(a.node)} ${drag} en ledning mere fra ${name(a.from)} til ${name(a.to)}.`;
          return `${rich(state)}${name(a.node)} har kun én vej ind. Byg vej nr. 2 fra ${name(a.from)} til ${name(a.to)} ${forKr(a.cost)}, så den klarer et brud.`;
      }
      return '';
    case 'upgrade':
      if (a.node && a.partial !== undefined) return `${gets(a.node)} Opgradér ${line(a.line, a.node)}, ${kr(a.cost)}. Så når den op på ${a.partial} %, og der skal mere til.`;
      if (a.node) return `${gets(a.node)} Opgradér ${line(a.line, a.node)}, ${kr(a.cost)}.`;
      if (a.green) return `${rich(state)}Vinden har mere at give, men ${line(a.line, null)} er fuld. Opgradér den ${forKr(a.cost)}, så stiger den grønne andel.`;
      return `${line(a.line, null).replace(/^l/, 'L')} er næsten fuld. Opgradér den i god tid, ${kr(a.cost)}.`;
    case 'station': {
      // Spor: tillid (IGN runde 4: "Kolding har ikke flere pladser ... så Fredericia kan komme på", men
      // Fredericia var på i forvejen og manglede en vej mere). Sig det, byen faktisk mangler.
      const tail = a.for === a.node ? 'så den kan få en vej mere ind' : hasAnyLine(state, a.for) ? `så ${name(a.for)} kan få en vej mere ind` : `så ${name(a.for)} kan komme på`;
      return `${name(a.node)} har ikke flere pladser. Tryk på ${name(a.node)} og byg en station ${forKr(a.cost)}, ${tail}.`;
    }
    case 'save':
      if (a.what === 'upgrade' && a.line) return `${gets(a.node)} Spar op til ${a.need} kr og opgradér ${line(a.line, a.node)}.`;
      if (a.what === 'station') return `${name(a.node)} venter på en plads. Spar op til ${a.need} kr til en station.`;
      if (nodeById(state, a.node)?.producer) return `${name(a.node)} venter på en ledning. Spar op til ${a.need} kr.`;
      return `${name(a.node)} venter på strøm. Spar op til ${a.need} kr.`;
    case 'calm':
      if (a.lack) return `${gets(a.node)} Der er ikke mere strøm at hente lige nu. Vind og sol kommer igen, eller byg mere produktion.`;
      if (a.soon) return `Strømmen er på vej til ${name(a.node)}. Den tænder om et øjeblik.`;
      // Kun i øvelsesåret, hvor urene står stille (IGN runde 1: teksten kom også i de år, der tæller).
      if (clocksFrozen(state, levelOf(state))) return `Vinden løjer lidt af, så ${name(a.node)} får lidt for lidt. Bare rolig, i år tæller det ikke.`;
      return `${gets(a.node)} Hold øje med den, og byg en vej mere ind, hvis den bliver ved.`;
    case 'praise': {
      if (a.dark) return `${name(a.node)} er mørk et øjeblik og tænder igen, når den får strøm. Hold de andre byer tændt imens.`;
      const goal = greenGoal(levelOf(state).greenGoalPct, state.difficulty);
      const g = Math.floor(greenPct(state));
      if (state.year > 0 && g < goal) return `${name(a.node)} lyser, men kun ${g} % af strømmen er grøn. Målet er ${goal} %: kobl mere vind og sol på.`;
      if (a.next && nodeById(state, a.next)?.kind === 'datacenter') {
        const mw = nodeById(state, a.next)?.city?.baseMw ?? 0;
        return `${name(a.node)} lyser! Snart kommer ${name(a.next)} og vil have ${mw} MW. Hav en ledig plads og strøm til overs i nærheden.`;
      }
      return a.next ? `${name(a.node)} lyser! Snart vil ${name(a.next)} også have strøm.` : `${name(a.node)} lyser! Hold lyset tændt året ud.`;
    }
  }
}
